import {
  Align,
  Button,
  Container,
  Distribute,
  IconButton,
  Loader,
  Spacer,
  SvgClose,
  SvgExternalLink,
  SvgInfo,
  Text,
  TextButton,
  Tooltip,
} from '@loomhq/lens';
import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestVideoPlayerSeekToTs } from 'actions/videoPlayer';
import { selectInsightsView } from 'actions/viewerInsights';
import { NOTIFICATION_URL_PARAMS } from 'constants/notifications';
import { EI_HOW_WE_CALCULATE } from 'constants/routes';
import { getParam, removeParam } from 'utilities/url';

import { usePrevious } from 'hooks/usePrevious';
import {
  selectVideo,
  selectVideoDuration,
  selectVideoViews,
} from 'selectors/videoPlayer';
import { selectActiveViewer, selectViewers } from 'selectors/viewerInsights';

import Details from './details';
import useFetchAndPopulateViewerInsights from './useFetchAndPopulateViewerInsights';
import ReactionsTimeline from '../../video-player/play-screen/play-screen-timeline';
import {
  trackBackToList,
  trackInfoIcon,
  trackInfoLink,
  trackPartialInfoAck,
  trackViewerDetails,
} from './analytics';
import { Reaction, ViewerInfo } from './components';
import { getHidePartialInfoStored, hidePartialInfo } from './localStorage';
import { toPercent } from './utils';

import ErrorBoundary from 'components/ui-components/error-boundary';

import '../../engagement-insights/styles.less';

import styles from './styles.module.less';

const { reactor } = NOTIFICATION_URL_PARAMS;

const ViewerInsightsList = props => {
  const dispatch = useDispatch();
  const slideableContainerRef = useRef();
  const containerRef = useRef();
  const [showPartialInfo, setShowPartialInfo] = useState(
    !getHidePartialInfoStored()
  );

  const saveSelected = useCallback(
    view => {
      dispatch(selectInsightsView(view));
      view && trackViewerDetails();
    },
    [dispatch]
  );

  const onPartialInfoAck = () => {
    setShowPartialInfo(false);
    hidePartialInfo();
    trackPartialInfoAck();
  };

  const slideableEl = slideableContainerRef?.current;
  const sectionEl = containerRef?.current;

  const onBack = useCallback(() => {
    slideableEl?.classList.remove(styles.shrinked);

    setTimeout(() => {
      document.documentElement.scrollTo({ top: 0 });
      slideableEl?.scrollTo({ left: 0, behavior: 'smooth' });
      sectionEl?.scrollTo({ top: 0 });
    }, 50);

    trackBackToList();

    setTimeout(() => saveSelected(null), 350);
  }, [saveSelected, sectionEl, slideableEl]);

  const { id: videoId, cta: { enabled: videoHasCta = false } = {} } =
    useSelector(selectVideo);
  const users = useSelector(selectViewers);
  const views = useSelector(selectVideoViews);
  const selected = useSelector(selectActiveViewer);
  const totalViews = parseInt(views.total || 0, 10);

  const previousSelected = usePrevious(selected);

  const withWatchSession =
    users.filter(user => user.hasWatchSession !== false) || [];
  const hasWatchSessions = Boolean(withWatchSession.length);
  const cta = withWatchSession.filter(({ cta }) => cta) || [];
  const hasPartialData = withWatchSession.length !== users.length;
  const partialData = hasPartialData && [withWatchSession.length, users.length];
  const ctaConversion = Math.min(
    (cta.length * 100) / withWatchSession.length,
    100
  );
  const completionRateAvg =
    withWatchSession
      .map(({ percentCompleted }) => percentCompleted)
      .reduce((p, c) => p + c, 0) / withWatchSession.length;

  useEffect(() => {
    const reactorId = getParam(reactor);

    if (reactorId) {
      const viewer = users.find(u => String(u.user.id) == reactorId);

      if (viewer != null) {
        saveSelected(viewer);
        removeParam(window)(reactor);
      }
    }

    containerRef?.current?.scrollTo?.({ top: 0 });

    if (!selected && !reactorId) {
      onBack();
    }
  }, [onBack, saveSelected, selected, users]);

  useFetchAndPopulateViewerInsights(videoId);

  return (
    <section
      ref={containerRef}
      className={
        styles.containerBox + ' overflow:auto thin-scrollbars radius:medium'
      }
    >
      <ErrorBoundary>
        <Distribute alignment="center" isSpread className={styles.sticky}>
          <Text variant="title">Engagement Insights</Text>
          <IconButton
            altText="Close"
            icon={<SvgClose />}
            onClick={props.onExit}
          />
        </Distribute>
        {hasPartialData && showPartialInfo && (
          <div className="mb:medium">
            <PartialDataBox onClick={onPartialInfoAck} />
          </div>
        )}
        <div className={styles.slideableContainer} ref={slideableContainerRef}>
          <section className={styles.slideable}>
            <TextButton
              className="mt:medium"
              size="small"
              icon={<SvgExternalLink />}
              onClick={() => {
                window.open(EI_HOW_WE_CALCULATE, '_blank').opener = null;
                trackInfoLink();
              }}
            >
              See how we calculate this
            </TextButton>
            <ul
              className={classnames(styles.viewsMetadata, {
                [styles.partial]: !hasPartialData,
              })}
            >
              <TotalViews total={totalViews} />
              <PercentCompleted
                percent={hasWatchSessions && completionRateAvg}
                partialData={partialData}
              />
              {videoHasCta && (
                <CtaConversion percent={hasWatchSessions && ctaConversion} />
              )}
            </ul>

            <Viewers users={users} setSelected={saveSelected} />
            {!users.length && <Loader />}
          </section>
          {selected && (
            <Details
              viewer={selected}
              onBack={onBack}
              // we skip animation if selection was made before we rendered
              // if selected in the UI, previousSelected would be null, not undefined
              skipAnimation={previousSelected === undefined}
            />
          )}
        </div>
      </ErrorBoundary>
    </section>
  );
};

const TotalViews = ({ total }) => (
  <li className="bgc:blurpleMedium">
    <Text fontWeight="medium">Total video views</Text>
    <Text size="xxlarge" fontWeight="medium">
      {total}
    </Text>
  </li>
);

const PercentCompleted = ({ percent, partialData }) => (
  <li className={classnames('bgc:blueLight', { [styles.disabled]: !percent })}>
    <Distribute gap="xsmall" direction="column">
      <Text fontWeight="medium">Average Completion Rate</Text>
      <PartialDataLabel partialData={partialData} source="completion rate" />
    </Distribute>
    <BigNumber percent={percent} />
  </li>
);

const CtaConversion = ({ percent }) => (
  <li
    className={classnames('bgc:yellowLight', { [styles.disabled]: !percent })}
  >
    <Distribute gap="xsmall" direction="column">
      <Text fontWeight="medium">Call-to-Action Conversion</Text>
    </Distribute>
    <BigNumber percent={percent} />
  </li>
);

const InfoIcon = ({ href, source }) => {
  const onClick = () => trackInfoIcon({ source });

  return (
    <Tooltip
      content="Engagement is only tracked after upgrading to Business or Enterprise. Excludes views on our iOS app."
      placement="topCenter"
    >
      <a
        href={href}
        className={styles.infoIcon}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        <SvgInfo />
      </a>
    </Tooltip>
  );
};

const PartialDataLabel = ({ partialData, source }) => {
  if (!partialData) {
    return null;
  }

  const [tracked, total] = partialData;

  return (
    <div className="flex items:center">
      <Text size="small">
        Based on {tracked} of {total} viewers
      </Text>
      <InfoIcon href={EI_HOW_WE_CALCULATE} source={source} />
    </div>
  );
};

const PartialDataBox = ({ onClick }) => (
  <Container
    backgroundColor="backgroundSecondary"
    radius="medium"
    padding="medium"
  >
    <Text fontWeight="medium" className="mb:medium">
      This video includes partially tracked data.
    </Text>
    <Spacer bottom="medium">
      <Text>
        Engagement is only tracked after upgrading to Business or Enterprise.
        Excludes views on our iOS app.
      </Text>
    </Spacer>
    <Align alignment="centerRight">
      <Button variant="primary" onClick={onClick}>
        Got it
      </Button>
    </Align>
  </Container>
);

const BigNumber = ({ percent }) => (
  <Text size="xxlarge" fontWeight="medium">
    {percent > 0 ? toPercent(percent) : '--'}
  </Text>
);

const Viewers = ({ users, setSelected }) => (
  <div className={styles.viewers}>
    <Text fontWeight="medium" className="mb:medium">
      {users.length} Viewers
    </Text>
    {users.map(viewer => (
      <div
        className={classnames(styles.card)}
        key={viewer.user.id}
        onClick={() => setSelected(viewer)}
      >
        <ViewerInfo user={viewer.user} events={viewer.events} />
      </div>
    ))}
  </div>
);

export const ViewersInsightsTimeline = () => {
  const selectedViewer = useSelector(selectActiveViewer);

  const usersFilter = !selectedViewer ? [] : [selectedViewer.user];

  return <ReactionsTimeline filter={{ users: usersFilter }} />;
};

export const ViewersEventsTimeline = ({ selectedViewer }) => {
  const dispatch = useDispatch();
  const currentlySelectedViewer = useSelector(selectActiveViewer);
  const videoDuration = useSelector(selectVideoDuration);

  const playVideoAt = ts => e => {
    e.stopPropagation();

    if (currentlySelectedViewer !== selectedViewer) {
      dispatch(selectInsightsView(selectedViewer));
    }

    dispatch(requestVideoPlayerSeekToTs(ts));
  };

  return (
    <div className="reaction viewer__events-timeline">
      {(selectedViewer.events || []).map((event, i) => (
        <Reaction
          key={`event${i}`}
          {...event}
          onClick={playVideoAt(event.ts)}
          videoDuration={videoDuration}
        />
      ))}
    </div>
  );
};

export default ViewerInsightsList;
